import type { BrandingTheme } from '@palacete/shared/branding';
import type { SiteSettings } from '../../lib/types';

export type SiteTab = 'paginas' | 'apariencia' | 'idiomas' | 'contacto' | 'enlaces' | 'anuncios' | 'legal' | 'dominio';

export const SITE_TAB_ITEMS: { id: SiteTab; label: string }[] = [
  { id: 'paginas', label: 'Páginas' },
  { id: 'apariencia', label: 'Apariencia' },
  { id: 'idiomas', label: 'Idiomas' },
  { id: 'contacto', label: 'Contacto y redes' },
  { id: 'enlaces', label: 'Enlaces' },
  { id: 'anuncios', label: 'Anuncios' },
  { id: 'legal', label: 'Avisos legales' },
  { id: 'dominio', label: 'Dominio' },
];

export const SITE_TAB_CONTEXT: Record<SiteTab, { title: string; description: string; detail: string }> = {
  paginas: {
    title: '¿Qué son las páginas?',
    description: 'Cada página es una sección de tu sitio: Inicio, Nosotros, Contacto, etc. Desde aquí también editas el encabezado y pie de página.',
    detail: 'Guardar conserva los cambios en el panel. Usa "Publicar sitio" para que los visitantes vean la versión más reciente.',
  },
  apariencia: {
    title: '¿Qué controla la apariencia?',
    description: 'Define el aspecto visual del sitio: colores, tipografías, logos y favicon.',
    detail: 'Los cambios se aplican de forma global. Publica el sitio para que los visitantes los vean.',
  },
  idiomas: {
    title: '¿Qué controlan los idiomas?',
    description: 'Tu sitio está en español. Si tu público también habla inglés, puedes activar una versión en ese idioma con su propio selector para que los visitantes elijan.',
    detail: 'Tener tu sitio en más de un idioma es una función de pago. Al activarla, cada página podrá tener su versión en inglés.',
  },
  contacto: {
    title: '¿Qué van en Contacto y redes?',
    description: 'Tus medios de contacto y perfiles en redes sociales que quieres mostrar a los visitantes.',
    detail: 'Los datos de contacto pueden usarse en distintas partes del sitio. Las redes aparecen en el pie de página y en otros bloques.',
  },
  enlaces: {
    title: '¿Qué es la página de enlaces?',
    description: 'Una sola página con todos tus botones (WhatsApp, menú, cómo llegar, eventos…) para poner como enlace en tu biografía de Instagram. Reemplaza a servicios como Linktree.',
    detail: 'Edita aquí los botones y su orden, genera el código QR para tu local, y publica el sitio para que tus visitantes lo vean.',
  },
  anuncios: {
    title: '¿Qué son los anuncios?',
    description: 'Avisos destacados para tus visitantes: una oferta por tiempo limitado, el cierre temporal de una sucursal o cualquier noticia importante. Puedes mostrarlos como banner sobre el menú o como ventana emergente al entrar al inicio.',
    detail: 'Ponles una fecha de caducidad y desaparecen solos. También puedes pausarlos o eliminarlos cuando quieras. Publica el sitio para que los visitantes los vean.',
  },
  legal: {
    title: '¿Qué son los avisos legales?',
    description: 'El aviso de privacidad, los términos y condiciones y el aviso de cookies de tu negocio. En México el aviso de privacidad es obligatorio cuando recoges datos de tus visitantes.',
    detail: 'Empieza desde una plantilla con los datos de tu negocio y ajústala. Aparecen como enlaces en el pie de página de tu sitio.',
  },
  dominio: {
    title: '¿Qué controla el dominio?',
    description: 'Centraliza la compra, conexión y estado del dominio principal, además de los desvíos hacia la versión correcta del sitio.',
    detail: 'Cuando Cloudflare lo permite, el panel automatiza los pasos. Cuando no, te guía y guarda tu avance.',
  },
};

export interface ContactData {
  email: string;
  phone: string;
  whatsapp: string;
  whatsappFab: boolean;
  whatsappMessage: string;
  address: string;
  hours: string;
}

export interface SocialData {
  instagram: string;
  facebook: string;
  linkedin: string;
  twitter: string;
  youtube: string;
}

export type ThemeData = BrandingTheme;
export type ColorTheme = NonNullable<ThemeData['colorTheme']>;
export type DisplayFont = NonNullable<ThemeData['fontDisplay']>;
export type BodyFont = NonNullable<ThemeData['fontBody']>;
export type Density = NonNullable<ThemeData['density']>;
export type FontScale = NonNullable<ThemeData['fontScale']>;
export type CardStyle = NonNullable<ThemeData['cardStyle']>;
export type CornerRadius = NonNullable<ThemeData['cornerRadius']>;
export type CardBorderColor = NonNullable<ThemeData['cardBorderColor']>;
export type CardBorderWidth = NonNullable<ThemeData['cardBorderWidth']>;
export type SurfaceStyle = NonNullable<ThemeData['surfaceStyle']>;
export type BackgroundStyle = NonNullable<ThemeData['backgroundStyle']>;
export type GlassBlur = NonNullable<ThemeData['glassBlur']>;

export interface SiteSettingsState {
  settings: SiteSettings | null;
  saving: boolean;
  needsPublish: boolean;
  isSaasAdmin: boolean;
  theme: ThemeData;
  navLogoUrl: string;
  footerLogoUrl: string;
  footerLogoMobileUrl: string;
  navLogoShowName: boolean;
  footerLogoShowName: boolean;
  faviconUrl: string;
  contact: ContactData;
  social: SocialData;
  hasOfficesType: boolean | null;
}

export function emptyContact(): ContactData {
  return { email: '', phone: '', whatsapp: '', whatsappFab: true, whatsappMessage: '', address: '', hours: '' };
}

export function emptySocial(): SocialData {
  return { instagram: '', facebook: '', linkedin: '', twitter: '', youtube: '' };
}

export function emptyTheme(): ThemeData {
  return {};
}

export function parseJson<T>(json: string | null | undefined, fallback: T): T {
  try {
    return JSON.parse(json ?? '') as T;
  } catch {
    return fallback;
  }
}
